import { motion } from "framer-motion";
import { useState } from "react";
import {
    Instagram,
    Mic,
    Scissors,
    Plane,
    Clapperboard,
    ArrowUpRight,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";

const icons = [Instagram, Mic, Scissors, Plane, Clapperboard];
const images = [
    "https://images.unsplash.com/photo-1580852300513-9b50125bf293?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxzdHVkaW8lMjBjYW1lcmElMjBsZW5zJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzgzNTM5ODgxfDA&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/14540985/pexels-photo-14540985.jpeg",
    "https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxzdHVkaW8lMjBjYW1lcmElMjBsZW5zJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzgzNTM5ODgxfDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1702259970719-fc3b6448de9b",
    "https://images.pexels.com/photos/7301210/pexels-photo-7301210.jpeg",
];

const cardVariant = {
    hidden: { opacity: 0, y: 40 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Services() {
    const { t } = useI18n();
    const s = t.services;
    const [, setActive] = useState(null);

    return (
        <section
            id="services"
            className="section relative bg-[#050505]"
            data-testid="services-section"
        >
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-24">
                    <div>
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl font-light tracking-[-0.03em] leading-[1.02] max-w-3xl">
                            {s.titlePre}
                            <span className="italic font-extralight text-white/60">
                                {s.titleAccent}
                            </span>
                        </h2>
                    </div>
                    <p className="font-body text-white/50 max-w-sm text-base leading-relaxed">
                        {s.lede}
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid="services-grid"
                >
                    {s.cards.map((c, i) => {
                        const Icon = icons[i] || Clapperboard;
                        const n = String(i + 1).padStart(2, "0");
                        return (
                            <motion.article
                                key={i}
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardVariant}
                                onHoverStart={() => setActive(n)}
                                onHoverEnd={() => setActive(null)}
                                className="service-card group relative flex flex-col justify-between min-h-[480px] overflow-hidden"
                                data-testid={`service-card-${n}`}
                            >
                                <div
                                    aria-hidden
                                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                >
                                    <img
                                        src={images[i]}
                                        alt=""
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/50" />
                                </div>

                                <div className="relative p-8 md:p-10 flex-1 flex flex-col justify-between">
                                    <div className="flex items-start justify-between">
                                        <div className="w-12 h-12 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/80 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/40 transition-colors duration-500">
                                            <Icon size={20} strokeWidth={1.5} />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.22em] bg-white/[0.04] border border-white/10 text-white/60 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/30 transition-colors duration-500">
                                                {c.tag}
                                            </span>
                                            <span className="font-mono text-[11px] tracking-[0.28em] text-white/30">
                                                {n}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-16">
                                        <h3 className="font-heading text-2xl md:text-[26px] font-medium tracking-[-0.02em] text-white leading-snug">
                                            {c.title}
                                        </h3>
                                        <p className="mt-4 font-body text-[15px] text-white/60 leading-relaxed max-w-sm">
                                            {c.body}
                                        </p>

                                        <ul className="mt-6 space-y-1.5">
                                            {c.deliverables.map((d) => (
                                                <li
                                                    key={d}
                                                    className="flex items-center gap-3 font-mono text-[11px] tracking-[0.14em] uppercase text-white/50"
                                                >
                                                    <span className="w-3 h-px bg-[#E55B13]" />
                                                    {d}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-8 flex items-center gap-2 text-white/80 group-hover:text-[#E55B13] transition-colors">
                                            <a
                                                href="#pricing"
                                                className="font-mono text-[11px] tracking-[0.24em] uppercase"
                                                data-testid={`service-card-${n}-pricing-link`}
                                            >
                                                {s.seePlans}
                                            </a>
                                            <ArrowUpRight
                                                size={14}
                                                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
